/**
 * Client-side smart item search — the mirror of the backend `common/item-search`.
 * Used by pickers that already hold the full catalogue in memory (e.g. the
 * enquiry item picker) so their type-ahead behaves exactly like the server
 * lists: token + fuzzy across name, code, barcode, colour, size and category.
 */

const SYNONYMS: Record<string, string> = {
  grey: 'gray',
  gray: 'gray',
  colour: 'color',
  color: 'color',
  mtr: 'meter',
  metre: 'meter',
  meter: 'meter',
};

export function normalizeText(s: string | null | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/[×x]/g, 'x')
    .replace(/[_/,\-|]+/g, ' ')
    .replace(/(\d)\s*(cm|mm|inch|in|ft)\b/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

const fold = (w: string) => SYNONYMS[w] ?? w;

export function tokenize(query: string): string[] {
  return normalizeText(query).split(' ').filter(Boolean).map(fold);
}

/** Build the searchable words for one item from any subset of these fields. */
export function haystackWords(fields: Array<string | null | undefined>): string[] {
  const words = normalizeText(fields.filter(Boolean).join(' ')).split(' ').filter(Boolean).map(fold);
  // Index size parts too: "60x150" → also "60" and "150".
  const out: string[] = [];
  for (const wd of words) {
    out.push(wd);
    if (/\dx\d/.test(wd)) for (const p of wd.split('x')) if (p) out.push(p);
  }
  return out;
}

function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

function fuzzyBudget(len: number): number {
  if (len <= 3) return 0;
  if (len <= 6) return 1;
  return 2;
}

function tokenWordScore(token: string, word: string): number {
  if (word === token) return 4;
  // Pure-number tokens (size parts like "34"/"150") match only a WHOLE word —
  // never a substring of a code/barcode, never fuzzily onto a neighbouring size.
  if (/^\d+$/.test(token)) return 0;
  if (word.startsWith(token)) return 3;
  if (word.includes(token)) return 2;
  // Fuzzy only for tokens with no digit (spelling typos); numeric/size tokens exact.
  if (!/\d/.test(token)) {
    const budget = fuzzyBudget(token.length);
    if (budget > 0 && editDistance(token, word, budget) <= budget) return 1;
  }
  return 0;
}

/** null = a token failed to match (AND); otherwise higher = better. */
export function scoreItem(queryTokens: string[], words: string[]): number | null {
  if (!queryTokens.length) return 0;
  let total = 0;
  for (const t of queryTokens) {
    let best = 0;
    for (const w of words) {
      const s = tokenWordScore(t, w);
      if (s > best) best = s;
      if (best === 4) break;
    }
    if (best === 0) return null;
    total += best;
  }
  return total;
}
